import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const sectionAnim = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.4, ease: 'easeOut' },
};

/**
 * PdfCtaBand
 * ----------
 * Banner between sections with a CTA button to download the full PDF report.
 *
 * The visitor already submitted their name + mobile via the blocking entry
 * modal, so we no longer ask for details at download time. The parent
 * component (`LandingPage` in App.js) transparently updates that lead with
 * the visitor's current calculation data before the PDF is generated.
 */
export const PdfCtaBand = ({ onDownloadPdf, result }) => {
  const handleDownloadClick = async () => {
    if (!result) {
      toast.error('Please calculate first before downloading.');
      return;
    }

    toast.loading('Preparing your sustainability report...');
    const success = await onDownloadPdf();
    toast.dismiss();

    if (success) {
      toast.success(
        <div>
          <strong>Report downloaded!</strong>
          <br />
          <small>Check your Downloads folder (Ctrl+J / Cmd+J)</small>
        </div>,
        { duration: 6000 },
      );
    } else {
      toast.error('Failed to generate report. Please try again.');
    }
  };

  return (
    <section className="py-10 sm:py-14 bg-secondary border-y border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          {...sectionAnim}
          className="flex flex-col sm:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2.5 rounded-xl bg-accent/10">
              <Sparkles size={24} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-primary">
                Get Your Detailed Sustainability Report
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Download a comprehensive PDF with your inputs, assumptions,
                year-by-year schedule, and complete analysis.
              </p>
              {/* Disclaimer */}
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-900">
                  <strong>Disclaimer:</strong> Mutual fund investments are
                  subject to market risks. Please read all scheme related
                  documents carefully.
                </p>
              </div>
            </div>
          </div>
          <Button
            size="lg"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl h-11 px-6 text-base font-semibold whitespace-nowrap active:scale-[0.98] shrink-0"
            onClick={handleDownloadClick}
            data-testid="pdf-download-report-button"
            style={{ transition: 'background-color 0.2s ease, box-shadow 0.2s ease' }}
          >
            <FileText size={18} className="mr-2" />
            Download PDF Report
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default PdfCtaBand;
